import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import api from "@/lib/api";
import { toast } from "sonner";
import { CalendarClock, Plus, Trash2, Play, Loader2, Send, History, ShieldCheck } from "lucide-react";

const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export function ScheduleDialog({ open, onOpenChange, dashboardId }) {
  const [schedules, setSchedules] = useState([]);
  const [f, setF] = useState({ format: "excel", frequency: "daily", hour_utc: 8, weekday: 0, recipients: "", body_mode: "attachment", per_recipient_rls: false });
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [runningId, setRunningId] = useState(null);

  const load = useCallback(() => api.get("/schedules", { params: { dashboard_id: dashboardId } }).then(({ data }) => setSchedules(data)).catch(() => {}), [dashboardId]);
  useEffect(() => { if (open) load(); }, [open, load]);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const create = async () => {
    setBusy(true);
    try {
      await api.post("/schedules", {
        dashboard_id: dashboardId, format: f.format, frequency: f.frequency,
        hour_utc: Number(f.hour_utc) || 8, weekday: Number(f.weekday) || 0,
        recipients: f.recipients.split(",").map((s) => s.trim()).filter(Boolean), enabled: true,
        body_mode: f.body_mode, per_recipient_rls: f.per_recipient_rls,
      });
      toast.success("Schedule created");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const testSend = async () => {
    setTesting(true);
    try {
      const { data } = await api.post("/schedules/test-send", {
        dashboard_id: dashboardId, format: f.format,
        recipients: f.recipients.split(",").map((s) => s.trim()).filter(Boolean),
      });
      toast.success(data.detail || "Test sent");
    } catch (e) { toast.error(e?.response?.data?.detail || "Test send failed"); }
    finally { setTesting(false); }
  };
  const del = async (id) => { try { await api.delete(`/schedules/${id}`); load(); } catch { toast.error("Failed"); } };
  const runNow = async (id) => {
    setRunningId(id);
    try { const { data } = await api.post(`/schedules/${id}/run`); toast.success(`Report generated (${data.detail})`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Run failed"); }
    finally { setRunningId(null); }
  };
  const toggle = async (s, enabled) => {
    try {
      await api.patch(`/schedules/${s.id}`, { dashboard_id: s.dashboard_id, format: s.format, frequency: s.frequency, hour_utc: s.hour_utc, weekday: s.weekday || 0, recipients: s.recipients || [], enabled, body_mode: s.body_mode || "attachment", per_recipient_rls: !!s.per_recipient_rls });
      load();
    } catch { toast.error("Failed"); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2"><CalendarClock className="w-5 h-5 text-[color:var(--brand-ocean)]" />Scheduled report delivery</DialogTitle>
          <DialogDescription>Export this dashboard on a schedule — delivered in-app (notification + download) and by email if SMTP is configured.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 py-1">
          <div className="grid grid-cols-3 gap-2">
            <div><Label className="text-xs">Format</Label>
              <Select value={f.format} onValueChange={(v) => set("format", v)}>
                <SelectTrigger data-testid="schedule-format" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="excel">Excel</SelectItem><SelectItem value="csv">CSV (zip)</SelectItem><SelectItem value="pdf">PDF (data)</SelectItem><SelectItem value="pdf_snapshot">PDF snapshot</SelectItem><SelectItem value="html">HTML</SelectItem><SelectItem value="pptx">PowerPoint (.pptx)</SelectItem></SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs">Frequency</Label>
              <Select value={f.frequency} onValueChange={(v) => set("frequency", v)}>
                <SelectTrigger data-testid="schedule-frequency" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="hourly">Hourly</SelectItem><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem></SelectContent>
              </Select>
            </div>
            {f.frequency !== "hourly" && (
              <div><Label className="text-xs">Hour (UTC)</Label><Input data-testid="schedule-hour" type="number" min={0} max={23} className="mt-1.5" value={f.hour_utc} onChange={(e) => set("hour_utc", e.target.value)} /></div>
            )}
          </div>
          {f.frequency === "weekly" && (
            <div><Label className="text-xs">Day of week</Label>
              <Select value={String(f.weekday)} onValueChange={(v) => set("weekday", v)}>
                <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>{WEEKDAYS.map((d, i) => <SelectItem key={i} value={String(i)}>{d}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div><Label className="text-xs">Email recipients (optional, comma-separated)</Label>
            <Input data-testid="schedule-recipients" className="mt-1.5" placeholder="a@x.com, b@y.com" value={f.recipients} onChange={(e) => set("recipients", e.target.value)} />
          </div>
          <div className="grid grid-cols-2 gap-2 items-end">
            <div><Label className="text-xs">Email body</Label>
              <Select value={f.body_mode} onValueChange={(v) => set("body_mode", v)}>
                <SelectTrigger data-testid="schedule-body-mode" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="attachment">Attachment</SelectItem>
                  <SelectItem value="inline">Tables in email body (HTML)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-center gap-2 text-xs text-[color:var(--ink-2)] pb-2" title="Each recipient that has an account receives THEIR data view (row-level security applied per user)">
              <Switch data-testid="schedule-per-rls" checked={f.per_recipient_rls} onCheckedChange={(v) => set("per_recipient_rls", v)} />
              <ShieldCheck className="w-3.5 h-3.5 text-[color:var(--brand-mint)]" />Personalize per recipient (RLS)
            </label>
          </div>
          <div className="flex gap-2">
            <Button data-testid="schedule-create-btn" onClick={create} disabled={busy} className="flex-1 bg-[color:var(--brand-ocean)] text-white">{busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />}Add schedule</Button>
            <Button data-testid="schedule-test-send-btn" onClick={testSend} disabled={testing} variant="outline" title="Email a test export to the recipients above right now">{testing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}Send test now</Button>
          </div>

          {schedules.length > 0 && (
            <div className="space-y-2 pt-1">
              <Label className="text-xs text-[color:var(--ink-3)]">Existing schedules</Label>
              {schedules.map((s) => <ScheduleRow key={s.id} s={s} onToggle={toggle} onRun={runNow} onDelete={del} runningId={runningId} />)}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ScheduleRow({ s, onToggle, onRun, onDelete, runningId }) {
  const [runsOpen, setRunsOpen] = useState(false);
  const [runs, setRuns] = useState(null);
  const toggleRuns = async () => {
    if (!runsOpen) {
      try { const { data } = await api.get(`/schedules/${s.id}/runs`); setRuns(data); } catch { setRuns([]); }
    }
    setRunsOpen((v) => !v);
  };
  return (
    <div data-testid="schedule-row" className="rounded-lg border border-[color:var(--glass-border)] px-3 py-2 text-xs">
      <div className="flex items-center gap-2">
        <Switch checked={s.enabled} onCheckedChange={(v) => onToggle(s, v)} />
        <div className="flex-1 min-w-0">
          <div className="font-medium text-[color:var(--ink)] flex items-center gap-1.5 flex-wrap">
            {s.format} · {s.frequency}{s.frequency !== "hourly" ? ` @ ${s.hour_utc}:00 UTC` : ""}{s.frequency === "weekly" ? ` (${WEEKDAYS[s.weekday || 0]})` : ""}
            {s.body_mode === "inline" && <Badge variant="outline" className="text-[9px]">inline body</Badge>}
            {s.per_recipient_rls && <Badge className="bg-[color:rgba(0,194,168,0.14)] text-[color:var(--success)] border-0 text-[9px]"><ShieldCheck className="w-2.5 h-2.5 mr-0.5" />per-recipient RLS</Badge>}
          </div>
          <div className="text-[color:var(--ink-3)] truncate">
            {(s.recipients || []).length ? `→ ${s.recipients.join(", ")}` : "in-app only"} · next: {s.next_run_at ? new Date(s.next_run_at).toLocaleString() : "—"}
            {s.last_status && <span className={s.last_status.startsWith("ok") ? "" : " text-[color:var(--danger)]"}> · last: {s.last_status}</span>}
          </div>
        </div>
        <Button data-testid="schedule-runs-btn" size="icon" variant="ghost" className="h-7 w-7 text-[color:var(--ink-3)]" title="Execution history" onClick={toggleRuns}><History className="w-3.5 h-3.5" /></Button>
        <Button data-testid="schedule-run-btn" size="icon" variant="ghost" className="h-7 w-7 text-[color:var(--brand-ocean)]" onClick={() => onRun(s.id)} disabled={runningId === s.id}>{runningId === s.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />}</Button>
        <Button size="icon" variant="ghost" className="h-7 w-7 text-[color:var(--danger)]" onClick={() => onDelete(s.id)}><Trash2 className="w-3.5 h-3.5" /></Button>
      </div>
      {runsOpen && (
        <div className="mt-2 border-t border-[color:var(--glass-border)] pt-2 space-y-1" data-testid="schedule-runs-list">
          {runs === null && <div className="text-[color:var(--ink-3)]">Loading…</div>}
          {runs && !runs.length && <div className="text-[color:var(--ink-3)]">No runs yet.</div>}
          {(runs || []).map((r) => (
            <div key={r.id} className="flex items-center gap-2" data-testid="schedule-run-item">
              <span className={"w-1.5 h-1.5 rounded-full shrink-0 " + (r.status === "ok" ? "bg-[color:var(--success)]" : "bg-[color:var(--danger)]")} />
              <span className="text-[color:var(--ink-3)] whitespace-nowrap">{new Date(r.started_at).toLocaleString()}</span>
              <span className="text-[color:var(--ink-2)] truncate flex-1">{r.detail}</span>
              <span className="text-[color:var(--ink-3)] tabular-nums">{r.duration_ms} ms</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
